import axios from "axios";
import {
    getToken,
    updateToken,
    authRequired,
    assertAccess,
    getSessionStatus,
    subscribeSession,
    invalidarSessao,
    negarAcesso,
    confirmarAcesso,
    sessionError
} from "../shared/keycloak";

const ACCESS_CHECK = "api/v1/integracao/entrega/configuracao";

function createApi(baseURL) {
    const api = axios.create({baseURL});

    const pending = new Set();
    const deniedReads = new Set();
    const requestKey = (config) =>
        JSON.stringify([config.method, config.url, config.params]);
    let accessCheck;
    let revision = getSessionStatus().revision;
    subscribeSession(
        () => {
            const session = getSessionStatus();
            if (!session.hasAccess || session.revision !== revision) {
                pending.forEach((controller) => controller.abort());
                pending.clear();
                deniedReads.clear();
            }
            revision = session.revision;
        },
        {priority: true}
    );

    api.revalidateAccess = ({clearDenials = true} = {}) => {
        if (!accessCheck) {
            accessCheck = (async () => {
                if (
                    !getSessionStatus().hasAccess &&
                    getSessionStatus().authenticated
                )
                    negarAcesso();
                await updateToken(-1);
                assertAccess({probe: true});
                await api.get(ACCESS_CHECK, {_accessProbe: true});
                confirmarAcesso();
                if (clearDenials) deniedReads.clear();
            })().finally(() => {
                accessCheck = undefined;
            });
        }
        return accessCheck;
    };
    api.interceptors.request.use(async (config) => {
        assertAccess({probe: config._accessProbe});
        if (deniedReads.has(requestKey(config)))
            throw Object.assign(
                new Error(
                    "Esta consulta não está autorizada. Verifique o acesso no menu após a liberação."
                ),
                {
                    status: 403,
                    code: "OPERACAO_NAO_AUTORIZADA",
                    isPermissionError: true
                }
            );
        if (authRequired) {
            await updateToken(30);
            assertAccess({probe: config._accessProbe});
            if (!getToken()) {
                invalidarSessao();
                throw sessionError();
            }
            config.headers.Authorization = `Bearer ${getToken()}`;
        }
        config._sessionRevision = getSessionStatus().revision;
        const controller = new AbortController();
        const previousSignal = config._originalSignal || config.signal;
        const abort = () => controller.abort();
        previousSignal?.addEventListener("abort", abort, {once: true});
        if (previousSignal?.aborted) controller.abort();
        config._originalSignal = previousSignal;
        config.signal = controller.signal;
        config._release = () => {
            pending.delete(controller);
            previousSignal?.removeEventListener("abort", abort);
        };
        pending.add(controller);
        return config;
    });

    api.interceptors.response.use(
        (response) => {
            response.config._release?.();
            assertAccess({probe: response.config._accessProbe});
            if (
                response.config._sessionRevision !== getSessionStatus().revision
            )
                throw new axios.CanceledError(
                    "A permissão mudou durante a consulta."
                );
            return response;
        },
        async (error) => {
            const config = error.config;
            config?._release?.();
            if (axios.isCancel(error)) throw error;
            const status = error.response?.status || error.status;
            if (
                authRequired &&
                status === 401 &&
                config &&
                !error.isSessionError
            ) {
                if (
                    !config._sessionRetry &&
                    ["get", "head"].includes(config.method)
                ) {
                    config._sessionRetry = true;
                    // Respostas concorrentes do token anterior compartilham a renovação.
                    if (config.headers.Authorization === `Bearer ${getToken()}`)
                        await updateToken(-1);
                    assertAccess({probe: config._accessProbe});
                    return api(config);
                }
                invalidarSessao();
            }
            if (
                authRequired &&
                status === 403 &&
                config &&
                !error.isSessionError
            ) {
                if (
                    config._accessProbe ||
                    (config.method === "get" &&
                        config.url.split("?")[0] === ACCESS_CHECK)
                ) {
                    negarAcesso();
                } else {
                    // O backend também recusa módulos, ocorrências e autoria. Um 403 isolado
                    // só bloqueia o aplicativo se a consulta comum confirmar a recusa global.
                    try {
                        await api.revalidateAccess({clearDenials: false});
                    } catch {
                        /* Estado atualizado pela verificação. */
                    }
                    if (["get", "head"].includes(config.method))
                        deniedReads.add(requestKey(config));
                }
            }
            throw normalizeApiError(error);
        }
    );

    return api;
}

export function normalizeApiError(error) {
    const response = error?.response;
    if (error?.isSessionError || error?.isPermissionError)
        return {
            status: error.status,
            code: error.code,
            message: error.message,
            isSessionError: Boolean(error.isSessionError)
        };
    if ([401, 403].includes(Number(response?.status))) {
        const status = Number(response.status);
        return {
            status,
            code:
                status === 401 ? "SESSAO_EXPIRADA" : "OPERACAO_NAO_AUTORIZADA",
            message:
                status === 401
                    ? "Sua sessão expirou. Entre novamente para continuar. Operações de escrita não são repetidas automaticamente."
                    : getSessionStatus().status === "denied"
                    ? "O acesso ao conector foi recusado. Solicite a liberação e verifique o acesso novamente."
                    : "Seu usuário não tem permissão para esta operação. As demais funcionalidades autorizadas continuam disponíveis.",
            isRequestError: true
        };
    }

    if (!response) {
        return {
            status: 0,
            code: "FALHA_COMUNICACAO",
            message:
                "Não foi possível acessar o conector. Verifique sua conexão e tente novamente.",
            isRequestError: true
        };
    }

    const status = Number(response.status) || 500;
    const data = response.data;

    if (typeof data === "string") {
        return {
            status,
            code: "REQUISICAO_REJEITADA",
            message:
                data.trim() ||
                "O conector rejeitou a operação sem informar o motivo."
        };
    }

    if (data && typeof data === "object") {
        return {
            ...data,
            status: Number(data.status) || status,
            code: data.code || "REQUISICAO_REJEITADA",
            message:
                extrairMensagemApi(data) ||
                "O conector rejeitou a operação sem informar o motivo."
        };
    }

    return {
        status,
        code: "RESPOSTA_INVALIDA",
        message:
            "O conector retornou uma resposta inválida. Informe o ocorrido ao suporte."
    };
}

export function extrairMensagemApi(valor) {
    if (typeof valor === "string") {
        const texto = valor.trim();
        if (!texto) return "";

        try {
            return extrairMensagemApi(JSON.parse(texto)) || texto;
        } catch {
            return texto;
        }
    }

    if (Array.isArray(valor)) {
        return valor.map(extrairMensagemApi).filter(Boolean).join(" ");
    }

    if (!valor || typeof valor !== "object") return "";

    const errosDetalhados = extrairMensagemApi(valor.erros || valor.errors);
    if (errosDetalhados) return errosDetalhados;

    const mensagemDireta = [
        valor.message,
        valor.detail,
        valor.mensagem,
        valor.error
    ]
        .map((item) => `${item || ""}`.trim())
        .find(Boolean);
    if (mensagemDireta) return mensagemDireta;

    return extrairMensagemApi(valor.resposta);
}

export function createQueryFetcher(api) {
    return ({queryKey, signal}) => {
        const [url, config] =
            typeof queryKey === "string" ? [queryKey] : queryKey;
        return api(url, {...config, signal}).then((res) => res.data);
    };
}

export default createApi;
